
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/production-logger';

interface UseAutoLoadSessionProps {
  userId: string | null;
  currentSession: any;
  lastSessionId: string | null;
  fetchingSessions: boolean;
  userMetadata: any;
  loadSession: (sessionId: string) => Promise<void>;
  updateLastSession: (sessionId: string | null) => Promise<boolean>;
  onError: (error: unknown, context: string, requestId?: string) => void;
}

export const useAutoLoadSession = ({
  userId,
  currentSession,
  lastSessionId,
  fetchingSessions,
  userMetadata,
  loadSession,
  updateLastSession,
  onError
}: UseAutoLoadSessionProps) => {
  
  useEffect(() => {
    const autoLoadLastSession = async () => {
      // Early returns for conditions where we shouldn't auto-load
      if (!userId || currentSession || !lastSessionId || lastSessionId?.trim() === '' || fetchingSessions) {
        return;
      }
      
      if (userMetadata && userMetadata.auto_load_last_session === false) {
        return;
      }
      
      try {
        if (lastSessionId && lastSessionId.trim() !== '') {
          logger.debug('Auto-loading last session', { 
            sessionId: lastSessionId,
            userId: userId,
            component: 'useAutoLoadSession'
          });
          
          const { data, error } = await supabase
            .from('assistant_sessions')
            .select('id')
            .eq('id', lastSessionId)
            .eq('user_id', userId)
            .single();
            
          if (error) {
            logger.error('Error checking if last session exists', {
              error: error.message,
              sessionId: lastSessionId,
              userId: userId,
              component: 'useAutoLoadSession'
            });
            
            if (error.code === 'PGRST116') {
              logger.info('Last session no longer exists, clearing lastSessionId', {
                sessionId: lastSessionId,
                component: 'useAutoLoadSession'
              });
              await updateLastSession(null);
            }
            return;
          }
          
          if (data) {
            await loadSession(lastSessionId);
          }
        }
      } catch (error) {
        onError(error, 'auto-load session');
      }
    };
    
    autoLoadLastSession();
  }, [userId, currentSession, lastSessionId, fetchingSessions, loadSession, userMetadata, updateLastSession, onError]);
};
